/**
 * Vera Finance route tree, composable under any parent path.
 *
 * Shell mounts it at `/finance/*`. The `financeRoutes(basePath)` factory
 * prefixes sidebar links with the mount path; route-object paths stay relative
 * so React Router resolves them against the mount point.
 *
 * The module is the home for cross-group / portfolio / authoring / PHI-gated
 * financial work (see docs/design/finance/PLAN.md, "Where it lives"): monthly
 * monitoring, stop-loss renewals, controls, and the fee registry. Group-scoped
 * read-only consumption lives on the Ops group detail page, not here.
 */

import { lazy, Suspense, createContext, useContext } from 'react';
import { Outlet, type RouteObject } from 'react-router-dom';
import { Spinner } from '@fluentui/react-components';
import { AppShell, type NavEntry } from '@widgets/app-shell';
import { ErrorBoundary } from '@shared/ui';
import { ProtectedRoute } from '@shared/auth';
import {
  BoardRegular,
  DocumentBulletListRegular,
  DatabaseRegular,
  ClipboardTaskListLtrRegular,
  MoneyRegular,
  SettingsRegular,
} from '@fluentui/react-icons';

const FinanceHomePage = lazy(() => import('@finance/pages/home').then((m) => ({ default: m.FinanceHomePage })));
const MonitoringPage = lazy(() => import('@finance/pages/monitoring').then((m) => ({ default: m.MonitoringPage })));
const MonitoringReviewPage = lazy(() => import('@finance/pages/monitoring').then((m) => ({ default: m.MonitoringReviewPage })));
const SourcesPage = lazy(() => import('@finance/pages/monitoring').then((m) => ({ default: m.SourcesPage })));
const SourceGroupPage = lazy(() => import('@finance/pages/monitoring').then((m) => ({ default: m.SourceGroupPage })));
const RenewalsPage = lazy(() => import('@finance/pages/renewals').then((m) => ({ default: m.RenewalsPage })));
const RegistryPage = lazy(() => import('@finance/pages/registry').then((m) => ({ default: m.RegistryPage })));
const GroupProfilePage = lazy(() => import('@finance/pages/registry').then((m) => ({ default: m.GroupProfilePage })));
const ConfigurationPage = lazy(() => import('@finance/pages/configuration').then((m) => ({ default: m.ConfigurationPage })));

function LazyPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<Spinner style={{ padding: '2rem' }} label="Loading..." />}>{children}</Suspense>;
}

const FinanceBasePathContext = createContext<string>('');

function buildNavItems(basePath: string): NavEntry[] {
  const p = (suffix: string) => `${basePath}${suffix}` || '/';
  return [
    { path: p('/'), label: 'Overview', icon: BoardRegular },
    // Workstream A: the monthly close board, and the feed/roster page behind it
    // (which is also where a group is enrolled, via the Group Info sync).
    { kind: 'section', label: 'Financial Monitoring' },
    { path: p('/monitoring'), label: 'Monitoring', icon: DocumentBulletListRegular },
    { path: p('/monitoring/sources'), label: 'Data Sources & Groups', icon: DatabaseRegular },
    { kind: 'section', label: 'Renewals & Fees' },
    { path: p('/renewals'), label: 'Stop-Loss Renewals', icon: ClipboardTaskListLtrRegular },
    { path: p('/registry'), label: 'Fee Registry', icon: MoneyRegular },
    // Book-wide settings. Finance had none of these in one place until 8/24:
    // each had been dropped into whatever functional page happened to need it.
    { kind: 'section', label: 'Admin' },
    { path: p('/configuration'), label: 'Configuration', icon: SettingsRegular },
  ];
}

export function FinanceRootLayout() {
  const basePath = useContext(FinanceBasePathContext);
  const navItems = buildNavItems(basePath);
  return (
    <ProtectedRoute appRolePrefix="Finance">
      <ErrorBoundary>
        <AppShell appId="finance" headerColor="#597B4B" navItems={navItems}>
          <Outlet />
        </AppShell>
      </ErrorBoundary>
    </ProtectedRoute>
  );
}

/**
 * Returns the Finance route tree, sidebar links prefixed for the mount path.
 * Shell: `financeRoutes('/finance')`.
 */
export function financeRoutes(basePath: string = ''): RouteObject[] {
  return [
    {
      element: (
        <FinanceBasePathContext.Provider value={basePath}>
          <FinanceRootLayout />
        </FinanceBasePathContext.Provider>
      ),
      children: [
        { index: true, element: <LazyPage><FinanceHomePage /></LazyPage> },
        { path: 'monitoring', element: <LazyPage><MonitoringPage /></LazyPage> },
        { path: 'monitoring/sources', element: <LazyPage><SourcesPage /></LazyPage> },
        { path: 'monitoring/sources/:client', element: <LazyPage><SourceGroupPage /></LazyPage> },
        { path: 'monitoring/:client/:period', element: <LazyPage><MonitoringReviewPage /></LazyPage> },
        { path: 'renewals', element: <LazyPage><RenewalsPage /></LazyPage> },
        { path: 'registry', element: <LazyPage><RegistryPage /></LazyPage> },
        { path: 'registry/:groupConfigId', element: <LazyPage><GroupProfilePage /></LazyPage> },
        { path: 'configuration', element: <LazyPage><ConfigurationPage /></LazyPage> },
      ],
    },
  ];
}
